import React from 'react'
import { ThemeProvider } from '@mui/material'

import { TCustomization } from 'types/GlobalCustomization'
import { TWidgetSettings } from 'types/GlobalWidget'

import { useSegmentBy } from 'redux/hooks/AttributeOptions'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'

import IconTip from 'components_new/molecules/IconTip'

import { getColorMode, getTheme } from 'themes'

interface BreakdownBannerProps {
  customization?: TCustomization
  customSegmentBy: string | null
  fullSize?: boolean
  onClear: () => void
  widgetSettings: TWidgetSettings | null // @TODO
}

const BreakdownBanner = (props: BreakdownBannerProps) => {
  const { customization, customSegmentBy, fullSize, onClear, widgetSettings } =
    props

  const { t } = useTranslation()

  const mode = customization?.background_colors?.paper
    ? getColorMode(customization?.background_colors?.paper)
    : 'light'

  const segmentOption = useSegmentBy(widgetSettings, customSegmentBy)
  const customSegmentOption = customSegmentBy ? segmentOption : null

  return (
    <Collapse in={!!customSegmentOption} unmountOnExit={true}>
      <ThemeProvider theme={getTheme(mode === 'light' ? 'dark' : 'light')}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexGrow: 0,
            flexShrink: 0,
            width: '100%',
            bgcolor: 'background.default',
            px: 2,
            py: 1,
            mb: fullSize ? undefined : 2,
            borderRadius: 1
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: -0.25
              }}
            >
              <Text
                color="text.secondary"
                variant="caption"
                sx={{ cursor: 'default', opacity: 0.8 }}
              >
                {t('widgets', 'custom_segment_by')}
              </Text>
              <IconTip
                size="small"
                title={
                  <>
                    <Text fontWeight="bold" gutterBottom={true} variant="body2">
                      {t('widgets', 'custom_segment_by')}
                    </Text>
                    <Text gutterBottom variant="body2">
                      {t('widgets', 'custom_segment_by_description')}
                    </Text>
                    <Text variant="body2">
                      {t('widgets', 'custom_segment_by_headers_hidden')}
                    </Text>
                  </>
                }
              />
            </Box>
            <Text
              color="text.primary"
              fontWeight="medium"
              sx={{ cursor: 'default' }}
            >
              {customSegmentOption?.name || ''}
            </Text>
          </Box>
          <Box>
            <IconButton
              edge="end"
              onClick={onClear}
              onMouseDown={(event) => event.stopPropagation()}
              size="small"
              sx={{
                '&:hover': {
                  color: 'text.primary'
                }
              }}
            >
              <Icon fontSize="small" name="Cancel" />
            </IconButton>
          </Box>
        </Box>
      </ThemeProvider>
    </Collapse>
  )
}

export default BreakdownBanner
